import { useForm } from "@inertiajs/react";
import { type FormEvent, useState } from "react";
import ConfirmStartMappingDialog from "@/components/domains/ConfirmStartMappingDialog";
import AuthOption from "@/components/forms/AuthOption";
import TextArea from "@/components/forms/TextArea";
import TextField from "@/components/forms/TextField";
import type { DomainTestUser, TestUserAuthMethod } from "@/types";

type Props = {
  domainId: string;
  testUser: DomainTestUser;
  onCancel?: () => void;
  onSaved?: () => void;
};

export default function DomainTestUserForm({ domainId, testUser, onCancel, onSaved }: Props) {
  const [confirming, setConfirming] = useState(false);
  const { data, errors, patch, processing, setData, transform } = useForm({
    auth_method: (testUser.auth_method ?? "credentials") as TestUserAuthMethod,
    email: testUser.email ?? "",
    password: "",
    instructions: testUser.instructions ?? "",
  });

  transform((form) => ({ test_user: form }));

  function save() {
    patch(`/domains/${domainId}/test_user`, {
      preserveScroll: true,
      preserveState: "errors",
      onSuccess: onSaved,
      onError: () => setConfirming(false),
    });
  }

  function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (testUser.starts_mapping_on_save) {
      setConfirming(true);
      return;
    }

    save();
  }

  return (
    <form className="mt-4 space-y-4" onSubmit={handleSubmit}>
      <div className="space-y-3">
        <AuthOption
          label="Email and password"
          selected={data.auth_method === "credentials"}
          onSelect={() => setData("auth_method", "credentials")}
        />
        <AuthOption
          label="Login instructions"
          selected={data.auth_method === "instructions"}
          onSelect={() => setData("auth_method", "instructions")}
        />
      </div>

      {data.auth_method === "credentials" ? (
        <div className="grid gap-4 sm:grid-cols-2">
          <TextField
            error={errors.email}
            label="Email"
            onChange={(value) => setData("email", value)}
            type="email"
            value={data.email}
          />
          <TextField
            error={errors.password}
            label="Password"
            onChange={(value) => setData("password", value)}
            placeholder={testUser.configured ? "Leave blank to keep the current password" : undefined}
            type="password"
            value={data.password}
          />
        </div>
      ) : (
        <TextArea
          error={errors.instructions}
          label="Login instructions"
          onChange={(value) => setData("instructions", value)}
          placeholder="How should we sign in? Keep it short."
          value={data.instructions}
        />
      )}

      <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
        {onCancel ? (
          <button type="button" className="btn" onClick={onCancel}>
            Cancel
          </button>
        ) : null}
        <button type="submit" className="btn btn-primary" disabled={processing}>
          {processing ? "Saving…" : "Save test user"}
        </button>
      </div>

      <ConfirmStartMappingDialog
        open={confirming}
        processing={processing}
        onCancel={() => setConfirming(false)}
        onConfirm={save}
      />
    </form>
  );
}
